import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import useBodyScrollLock from "@helpers/useBodyScrollLock";
import "./TradeModal.sass";

/**
 * Shell for the futures trade modals: centered card on desktop,
 * bottom sheet with a grab handle on mobile.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {string} [props.className] modifier for the panel (width, paddings)
 * @param {string} [props.labelledBy] id of the title element
 * @param {React.ReactNode} props.children
 */
export const TradeModal = ({
  isOpen,
  onClose,
  className = "",
  labelledBy,
  children,
}) => {
  // Over another locked sheet (the mobile trade form) the lock is left to it:
  // unlocking on close would let the page scroll under that sheet
  const ownsScrollLockRef = useRef(null);

  if (!isOpen) {
    ownsScrollLockRef.current = null;
  } else if (ownsScrollLockRef.current === null) {
    ownsScrollLockRef.current = document.body.style.position !== "fixed";
  }

  useBodyScrollLock(isOpen && ownsScrollLockRef.current);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || typeof document === "undefined") return null;

  // React events bubble out of portals: keep drags and wheels inside the modal
  // away from a parent's swipe handlers (the mobile trade form pages)
  const stopPropagation = (event) => event.stopPropagation();

  return createPortal(
    <div
      className="trade-modal"
      onPointerDown={stopPropagation}
      onPointerUp={stopPropagation}
      onPointerCancel={stopPropagation}
      onWheel={stopPropagation}
    >
      <div
        className="trade-modal__backdrop"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        className={`trade-modal__panel ${className}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
      >
        <span className="trade-modal__handle" aria-hidden="true" />
        {children}
      </div>
    </div>,
    document.body,
  );
};
